import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Animated, Easing, Image, TouchableOpacity, useWindowDimensions, View, ViewProps } from "react-native";
import { shallow } from "zustand/shallow";
import {
    Aoi,
    Aois,
    ArrowBack,
    ArrowForward,
    FeatureType,
    LoadingSpinner,
    LocalizedToken,
    Map,
    MapFilter,
    MapIcon,
    MapSettings,
    MapType,
    Path,
    PathDirection,
    PathCategoryId,
    PathBeltLift,
    PathCanoe,
    PathChairLift,
    PathCrossCountrySkiing,
    PathGondola,
    PathHiking,
    PathMountainBiking,
    PathRunning,
    PathRiding,
    PathSkiSlope,
    PathSnowmobiling,
    PathSurfaceLift,
    PoiBusStops,
    PoiCabins,
    PoiCafes,
    PoiCategoryId,
    PoiIceRink,
    PoiParking,
    PoiPlaceOfInterests,
    PoiRestaurants,
    PoiShelters,
    PoiStores,
    IconClose,
    PoiSwimmingPlaces,
    PoiToilets,
    PoiViewpoints,
    PoiWarnings,
    Paths,
    Poi,
    Pois,
    ThemedText,
    useThemedStyleFunction,
    withExternalStyle,
} from "@sodraberget/shared";
import { useDataStore, useStateStore } from "../../stores";
import { mapPanelStyle } from "./map-panel.style";
import { useTranslation } from "react-i18next";
import { getTranslatedProperty } from "../../localization";
import { getIcon, hasIcon } from "@sodraberget/map-content/src/icon-library/iconLibrary.generated";

const earthStandardIcon = require("../../../../shared/src/assets/images/icons/map/std.png");
const earthSateliteIcon = require("../../../../shared/src/assets/images/icons/map/sat.png");
const earth3DStandardIcon = require("../../../../shared/src/assets/images/icons/map/std3d.png");
const earth3DSateliteIcon = require("../../../../shared/src/assets/images/icons/map/sat3d.png");

// Renders a glyph from the shared icon library (admin-selected iconKey), cropped to the
// 32x32 safe area of the 48x48 canvas – same presentation as the mobile app's LibraryIcon.
const LibraryCategoryIcon = ({ iconKey, style }: { iconKey: string; style?: any }) => {
    const { width = 24, height = 24, color = '#476A89' } = style ?? {};
    const icon = getIcon(iconKey);
    if (!icon) {
        return null;
    }
    const body = icon.body.replace(/currentColor/g, color);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="8 8 32 32" fill="${color}">${body}</svg>`;
    const uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    return <Image source={{ uri }} style={{ width, height }} />;
};

const MapPanel = (props: ViewProps) => {
    const { style: styleProp } = props;
    const mapRef = useRef<any>();
    const initialFocusApplied = useRef(false);
    const { i18n, t } = useTranslation();
    const iso = i18n.language;
    const [mapType, setMapType] = useState<MapType>(MapType.Map);
    const [mapSelectorOpen, setMapSelectorOpen] = useState(false);
    const [typeFilterOpen, setTypeFilterOpen] = useState(false);
    const typeFilterAnimation = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        Animated.timing(typeFilterAnimation, {
            toValue: typeFilterOpen ? 1 : 0,
            duration: 400,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
        }).start();
    }, [typeFilterOpen]);
    const [selectedPathTypeIds, setSelectedPathTypeIds] = useState<string[] | undefined>(undefined);
    const [selectedPoiTypeIds, setSelectedPoiTypeIds] = useState<string[] | undefined>(undefined);
    const { height: windowHeight } = useWindowDimensions();

    const pathTypeIcons: Record<string, React.ComponentType<any>> = {
        [PathCategoryId.CrossCountrySkiing]: PathCrossCountrySkiing,
        [PathCategoryId.Snowmobiling]: PathSnowmobiling,
        [PathCategoryId.MountainBiking]: PathMountainBiking,
        [PathCategoryId.Hiking]: PathHiking,
        [PathCategoryId.Running]: PathRunning,
        [PathCategoryId.Riding]: PathRiding,
        [PathCategoryId.Canoe]: PathCanoe,
        [PathCategoryId.ChairLift]: PathChairLift,
        [PathCategoryId.Gondola]: PathGondola,
        [PathCategoryId.SurfaceLift]: PathSurfaceLift,
        [PathCategoryId.BeltLift]: PathBeltLift,
        [PathCategoryId.SkiSlope]: PathSkiSlope,
    };

    const poiTypeIcons: Record<string, React.ComponentType<any>> = {
        [PoiCategoryId.Restaurants]: PoiRestaurants,
        [PoiCategoryId.Cafes]: PoiCafes,
        [PoiCategoryId.Attractions]: PoiPlaceOfInterests,
        [PoiCategoryId.Viewpoints]: PoiViewpoints,
        [PoiCategoryId.Parkings]: PoiParking,
        [PoiCategoryId.Cabins]: PoiCabins,
        [PoiCategoryId.BusStops]: PoiBusStops,
        [PoiCategoryId.Shelters]: PoiShelters,
        [PoiCategoryId.Shops]: PoiStores,
        [PoiCategoryId.Toilets]: PoiToilets,
        [PoiCategoryId.Warnings]: PoiWarnings,
        [PoiCategoryId.SwimmingPlaces]: PoiSwimmingPlaces,
        [PoiCategoryId.IceRink]: PoiIceRink,
    };

    const {
        paths,
        pathGpsCoordinates,
        pathCategories,
        aois,
        aoiCategories,
        aoiGpsCoordinates,
        pois,
        poiCategories,
        mainContent,
        additionalMapContent,
    } = useDataStore(state => state, shallow);

    const {
        selectedType,
        selectedId,
        selectedSource,
        setSelected,
        filters,
        focusOnMainContent,
        pathDirection,
        setPathDirection,
    } = useStateStore(state => state, shallow);

    const matchesAreas = (objectAreas: LocalizedToken[], filterAreas?: string[]) => {
        if (!filterAreas || filterAreas.length === 0) return true;
        return objectAreas.some(a => {
            const names = Object.values(a.name as any) as string[];
            return names.some(n => filterAreas.some(f => f.toLowerCase() === n.toLowerCase()));
        });
    };

    const isCategoryVisible = (categoryId: string | number, mainCategories?: string[], additionalCategories?: string[]) => (
        (mainCategories?.length === 0)
        || (additionalCategories?.length === 0)
        || mainCategories?.includes(String(categoryId))
        || additionalCategories?.includes(String(categoryId))
    );

    const filteredPathsBase = useMemo(() => Object.values(paths ?? {})
        .filter((path: Path) => isCategoryVisible(path.pathCategoryId, mainContent.pathCategories, additionalMapContent?.pathCategories)
            && matchesAreas(path.areas, mainContent.areas)), [paths, mainContent.pathCategories, additionalMapContent?.pathCategories, mainContent.areas]);

    const filteredAoisBase = useMemo(() => Object.values(aois ?? {})
        .filter((aoi: Aoi) => isCategoryVisible(aoi.aoiCategoryId, mainContent.aoiCategories, additionalMapContent?.aoiCategories)
            && matchesAreas(aoi.areas, mainContent.areas)), [aois, mainContent.aoiCategories, additionalMapContent?.aoiCategories, mainContent.areas]);

    const filteredPoisBase = useMemo(() => Object.values(pois ?? {})
        .filter((poi: Poi) => isCategoryVisible(poi.poiCategoryId, mainContent.poiCategories, additionalMapContent?.poiCategories)
            && matchesAreas(poi.areas, mainContent.areas)), [pois, mainContent.poiCategories, additionalMapContent?.poiCategories, mainContent.areas]);

    const pathTypeOptions = useMemo(() => {
        const uniqueCategoryIds = [...new Set(filteredPathsBase.map((path: Path) => `${path.pathCategoryId}`))];
        return uniqueCategoryIds
            .map((categoryId) => {
                const category = pathCategories?.[categoryId];
                return {
                    id: categoryId,
                    name: category ? getTranslatedProperty('name', category) : categoryId,
                    iconKey: hasIcon(category?.iconKey) ? category?.iconKey : undefined,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [filteredPathsBase, pathCategories, iso]);

    const poiTypeOptions = useMemo(() => {
        // Events carry their own event category ids – they must not produce POI category buttons
        // (same logic as the mobile app's filter panel).
        const poisOnly = filteredPoisBase.filter((poi: Poi) => poi.sourceType !== 'event' && poi.eventCategoryId == null);
        const uniqueCategoryIds = [...new Set(poisOnly.map((poi: Poi) => `${poi.poiCategoryId}`))];
        return uniqueCategoryIds
            .map((categoryId) => {
                const category = poiCategories?.[categoryId];
                return {
                    id: categoryId,
                    name: category ? getTranslatedProperty('name', category) : categoryId,
                    iconKey: hasIcon(category?.iconKey) ? category?.iconKey : undefined,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [filteredPoisBase, poiCategories, iso]);

    useEffect(() => {
        const optionIds = pathTypeOptions.map((option) => option.id);
        setSelectedPathTypeIds((current) => {
            if (current === undefined) {
                return undefined;
            }

            const filtered = current.filter((id) => optionIds.includes(id));
            if (filtered.length === optionIds.length) {
                return undefined;
            }
            return filtered;
        });
    }, [pathTypeOptions]);

    useEffect(() => {
        const optionIds = poiTypeOptions.map((option) => option.id);
        setSelectedPoiTypeIds((current) => {
            if (current === undefined) {
                return undefined;
            }

            const filtered = current.filter((id) => optionIds.includes(id));
            if (filtered.length === optionIds.length) {
                return undefined;
            }
            return filtered;
        });
    }, [poiTypeOptions]);

    const filteredPathsForMap = useMemo(() => {
        if (selectedPathTypeIds === undefined) {
            return filteredPathsBase;
        }

        return filteredPathsBase.filter((path: Path) => selectedPathTypeIds.includes(`${path.pathCategoryId}`));
    }, [filteredPathsBase, selectedPathTypeIds]);

    const filteredPoisForMap = useMemo(() => {
        if (selectedPoiTypeIds === undefined) {
            return filteredPoisBase;
        }

        const visiblePoiIds = new Set(
            filteredPoisBase
                .filter((poi: Poi) => poi.sourceType !== 'event' && selectedPoiTypeIds.includes(`${poi.poiCategoryId}`))
                .map((poi: Poi) => poi.id),
        );

        // Events are not filtered by their own event category – they follow the POI they belong to.
        return filteredPoisBase.filter((poi: Poi) =>
            poi.sourceType === 'event'
                ? (poi.linkedPoiId != null && visiblePoiIds.has(poi.linkedPoiId))
                : visiblePoiIds.has(poi.id));
    }, [filteredPoisBase, selectedPoiTypeIds]);

    useEffect(() => {
        if (mapRef.current && (paths || pois || aois)) {
            const newFocus = [];

            if (paths) {
                const filteredPathsObj = filteredPathsForMap.reduce((obj, current) => {
                    obj[current.id] = current;
                    return obj;
                }, {} as Paths);

                mapRef.current.setPaths(filteredPathsObj, pathCategories, pathGpsCoordinates);

                if (focusOnMainContent) {
                    const focusPathIds = filteredPathsForMap
                        .filter((path: Path) => mainContent.pathCategories?.length === 0 || mainContent.pathCategories?.includes(path.pathCategoryId))
                        .map((path: Path) => path.id);

                    if (focusPathIds.length) {
                        newFocus.push({ type: FeatureType.Path, ids: focusPathIds });
                    }
                } else {
                    newFocus.push({ type: FeatureType.Path, ids: filteredPathsForMap.map((path: Path) => path.id) });
                }
            }

            if (aois) {
                const filteredAoiObj = filteredAoisBase.reduce((obj, current) => {
                    obj[current.id] = current;
                    return obj;
                }, {} as Aois);

                mapRef.current.setAois(filteredAoiObj, aoiCategories, aoiGpsCoordinates);

                if (focusOnMainContent) {
                    const focusAoiIds = filteredAoisBase
                        .filter((aoi: Aoi) => mainContent.aoiCategories?.length === 0 || mainContent.aoiCategories?.includes(aoi.aoiCategoryId))
                        .map((aoi: Aoi) => aoi.id);

                    if (focusAoiIds.length) {
                        newFocus.push({ type: FeatureType.Aoi, ids: focusAoiIds });
                    }
                } else {
                    newFocus.push({ type: FeatureType.Aoi, ids: filteredAoisBase.map((aoi: Aoi) => aoi.id) });
                }
            }

            if (pois) {
                const filteredPoisObj = filteredPoisForMap.reduce((obj, current) => {
                    obj[current.id] = current;
                    return obj;
                }, {} as Pois);

                mapRef.current.setPois(filteredPoisObj, poiCategories);

                if (focusOnMainContent) {
                    const focusPoiIds = filteredPoisForMap
                        .filter((poi: Poi) => mainContent.poiCategories?.length === 0 || mainContent.poiCategories?.includes(poi.poiCategoryId))
                        .map((poi: Poi) => poi.id);

                    if (focusPoiIds.length) {
                        newFocus.push({ type: FeatureType.Poi, ids: focusPoiIds });
                    }
                } else {
                    newFocus.push({ type: FeatureType.Poi, ids: filteredPoisForMap.map((poi: Poi) => poi.id) });
                }
            }

            if (newFocus.length) {
                const shouldRefocus = !initialFocusApplied.current && selectedType === undefined && selectedId === undefined;
                mapRef.current.setFocus(newFocus, shouldRefocus);
                initialFocusApplied.current = true;
            }

            mapRef.current.setSelected(selectedType, selectedId);
        }
    }, [
        paths,
        pois,
        aois,
        filteredPathsForMap,
        filteredAoisBase,
        filteredPoisForMap,
        pathCategories,
        pathGpsCoordinates,
        aoiCategories,
        aoiGpsCoordinates,
        poiCategories,
        selectedType,
        selectedId,
        focusOnMainContent,
        mainContent.pathCategories,
        mainContent.aoiCategories,
        mainContent.poiCategories,
    ]);

    useEffect(() => {
        const isThreeDMode = mapType === MapType.ThreeD || mapType === MapType.ThreeDSatelite;

        if (selectedSource === "map" && !isThreeDMode) {
            mapRef.current.setHighlighted(selectedType, selectedId);
        } else {
            mapRef.current.setSelected(selectedType, selectedId);
        }
    }, [selectedType, selectedId, selectedSource, mapType]);

    useEffect(() => {
        mapRef.current.setFilters(filters);
    }, [filters]);

    useEffect(() => {
        mapRef.current?.setSelectedLanguage(iso);
    }, [iso]);

    useEffect(() => {
        mapRef.current?.setMapType(mapType);
    }, [mapType]);

    useEffect(() => {
        mapRef.current?.setPathDirection(pathDirection, true);
    }, [pathDirection]);

    const onLoad = useCallback(() => {
        mapRef.current.setMapType(mapType);
        mapRef.current.setPathDirection(pathDirection);
    }, [mapRef, mapType, pathDirection]);

    const onMessage = (data: any) => {
        const { deselect, type, id } = data;
        if (deselect || (type !== undefined && id !== undefined)) {
            mapRef.current.setHighlighted(type, id);
            setSelected(type, id, "map");
        }
    };

    const onZoomIn = useCallback(() => mapRef.current.adjustZoom(1), [mapRef]);
    const onZoomOut = useCallback(() => mapRef.current.adjustZoom(-1), [mapRef]);
    const toggleMapSelector = useCallback(() => {
        setMapSelectorOpen(prev => {
            const next = !prev;
            if (next) {
                setTypeFilterOpen(false);
            }
            return next;
        });
    }, []);
    const toggleTypeFilter = useCallback(() => {
        setTypeFilterOpen(prev => {
            const next = !prev;
            if (next) {
                setMapSelectorOpen(false);
            }
            return next;
        });
    }, []);
    const togglePathDirection = useCallback(() => {
        setPathDirection(pathDirection === undefined ? PathDirection.Forward : undefined);
    }, [pathDirection, setPathDirection]);

    const togglePathType = useCallback((typeId: string) => () => {
        const allIds = pathTypeOptions.map((option) => option.id);
        setSelectedPathTypeIds((current) => {
            if (!allIds.length) {
                return undefined;
            }

            const selected = current === undefined ? [...allIds] : [...current];
            const hasType = selected.includes(typeId);
            const next = hasType
                ? selected.filter((id) => id !== typeId)
                : [...selected, typeId];

            if (next.length === allIds.length) {
                return undefined;
            }

            return next;
        });
    }, [pathTypeOptions]);

    const togglePoiType = useCallback((typeId: string) => () => {
        const allIds = poiTypeOptions.map((option) => option.id);
        setSelectedPoiTypeIds((current) => {
            if (!allIds.length) {
                return undefined;
            }

            const selected = current === undefined ? [...allIds] : [...current];
            const hasType = selected.includes(typeId);
            const next = hasType
                ? selected.filter((id) => id !== typeId)
                : [...selected, typeId];

            if (next.length === allIds.length) {
                return undefined;
            }

            return next;
        });
    }, [poiTypeOptions]);

    const typeFilterOptions = useMemo(() => {
        const pathOptions = pathTypeOptions.map((option) => ({
            key: `path-${option.id}`,
            id: option.id,
            onPress: togglePathType(option.id),
            Icon: pathTypeIcons[option.id] ?? MapIcon,
            iconKey: option.iconKey,
            active: selectedPathTypeIds === undefined || selectedPathTypeIds.includes(option.id),
            label: option.name,
        }));

        const poiOptions = poiTypeOptions.map((option) => ({
            key: `poi-${option.id}`,
            id: option.id,
            onPress: togglePoiType(option.id),
            Icon: poiTypeIcons[option.id] ?? PoiPlaceOfInterests,
            iconKey: option.iconKey,
            active: selectedPoiTypeIds === undefined || selectedPoiTypeIds.includes(option.id),
            label: option.name,
        }));

        return [...pathOptions, ...poiOptions];
    }, [pathTypeOptions, poiTypeOptions, togglePathType, togglePoiType, selectedPathTypeIds, selectedPoiTypeIds]);

    // Grow to two columns instead of scrolling when the menu would reach the top of the map.
    const filterButtonSize = 28;
    const filterButtonGap = 8;
    const filterMenuMaxHeight = windowHeight * 0.6;
    const filterRowsPerColumn = Math.max(1, Math.floor((filterMenuMaxHeight + filterButtonGap) / (filterButtonSize + filterButtonGap)));
    const filterColumnCount = Math.min(2, Math.max(1, Math.ceil(typeFilterOptions.length / filterRowsPerColumn)));
    const filterMenuWidth = filterColumnCount * filterButtonSize + (filterColumnCount - 1) * filterButtonGap;

    const style = useThemedStyleFunction(mapPanelStyle);
    const containerStyle = withExternalStyle(style.container, styleProp);

    const mapTypeOptions = [
        { type: MapType.Map, label: t("mapChooserStandard"), source: earthStandardIcon },
        { type: MapType.Satelite, label: t("mapTypeSatelite", { defaultValue: "Satellite" }), source: earthSateliteIcon },
        { type: MapType.ThreeD, label: t("mapChooserThreeDStandard"), source: earth3DStandardIcon },
        { type: MapType.ThreeDSatelite, label: t("mapChooserThreeDSatellite"), source: earth3DSateliteIcon },
    ];

    return (
        <View style={containerStyle}>
            <Map style={style.map} ref={(ref: any) => (mapRef.current = ref)} onLoad={onLoad} onMessage={onMessage} />

            <View style={style.controlsColumn} pointerEvents="box-none">
                {mapSelectorOpen && (
                    <View style={style.mapSelectorCard}>
                        <ThemedText style={style.mapSelectorTitle} type="bold">
                            {t("mapChooserTitle")}
                        </ThemedText>
                        <View style={style.mapTypeGrid}>
                            {mapTypeOptions.map((option) => {
                                const isActive = mapType === option.type;
                                return (
                                    <TouchableOpacity
                                        key={option.type}
                                        style={[style.mapTypeButton, isActive && style.mapTypeButtonActive]}
                                        accessibilityRole="button"
                                        accessibilityLabel={option.label}
                                        accessibilityState={{ selected: isActive }}
                                        onPress={() => {
                                            setMapType(option.type);
                                            setMapSelectorOpen(false);
                                        }}>
                                        <View style={style.mapTypePreviewFrame}>
                                            {isActive && <View style={style.mapTypeActiveMarker} />}
                                            <View style={[style.mapTypePreview, style.mapTypePreviewBorderless]}>
                                                <Image
                                                    source={option.source}
                                                    style={style.mapTypePreviewImage}
                                                    resizeMode="contain"
                                                />
                                            </View>
                                        </View>
                                        <ThemedText style={[style.mapTypeButtonText, isActive && style.mapTypeButtonTextActive]} type={isActive ? "bold" : "light"}>
                                            {option.label}
                                        </ThemedText>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                        <View style={style.mapSettingsDivider} />
                        <ThemedText style={style.mapSettingsTitle} type="bold">
                            {t("mapChooserSettingsTitle")}
                        </ThemedText>
                        <TouchableOpacity
                            style={style.directionButton}
                            onPress={togglePathDirection}
                            accessibilityRole="button"
                            accessibilityLabel={t("mapChooserDirection")}
                            accessibilityState={{ selected: pathDirection !== undefined }}>
                            <View style={style.mapTypePreviewFrame}>
                                {pathDirection !== undefined && <View style={style.mapTypeActiveMarker} />}
                                <View style={style.mapTypePreview}>
                                    <View style={style.directionArrows}>
                                        <ArrowBack style={style.directionArrowBack} />
                                        <ArrowForward style={style.directionArrowForward} />
                                    </View>
                                </View>
                            </View>
                            <ThemedText style={style.mapTypeButtonText}>
                                {t("mapChooserDirection")}
                            </ThemedText>
                        </TouchableOpacity>
                    </View>
                )}

                <View style={[style.filterControlWrap, typeFilterOpen && style.filterControlWrapOpen]}>
                    {typeFilterOpen && (
                        <Animated.View style={[style.filterIconMenu, {
                            opacity: typeFilterAnimation,
                            transform: [{ translateY: typeFilterAnimation.interpolate({ inputRange: [0, 1], outputRange: [16, 0] }) }],
                            width: filterMenuWidth,
                        }]}>
                            {typeFilterOptions.map((option) => {
                                const IconComponent = option.Icon;
                                const isPathType = option.key.startsWith('path-');
                                const buttonStyle = option.active
                                    ? (isPathType ? { ...style.filterIconMenuButton, ...style.filterIconMenuButtonPathActive } : { ...style.filterIconMenuButton, ...style.filterIconMenuButtonPoiActive })
                                    : (isPathType ? { ...style.filterIconMenuButton, ...style.filterIconMenuButtonInactive } : { ...style.filterIconMenuButton, ...style.filterIconMenuButtonPoiInactive });
                                const iconStyle = option.active
                                    ? (isPathType ? { ...style.filterIconMenuIcon, ...style.filterIconMenuIconPathActive } : { ...style.filterIconMenuIcon, ...style.filterIconMenuIconPoiActive })
                                    : (isPathType ? { ...style.filterIconMenuIcon, ...style.filterIconMenuIconInactive } : { ...style.filterIconMenuIcon, ...style.filterIconMenuIconPoiInactive });
                                return (
                                    <TouchableOpacity
                                        key={option.key}
                                        style={buttonStyle}
                                        onPress={option.onPress}
                                        accessibilityRole="button"
                                        accessibilityLabel={option.label}
                                        accessibilityState={{ selected: option.active }}>
                                        {option.iconKey
                                            ? <LibraryCategoryIcon iconKey={option.iconKey} style={iconStyle} />
                                            : <IconComponent style={iconStyle} />
                                        }
                                    </TouchableOpacity>
                                );
                            })}
                        </Animated.View>
                    )}

                    <TouchableOpacity
                        style={typeFilterOpen ? { ...style.filterFab, ...style.filterFabOpen } : style.filterFab}
                        onPress={toggleTypeFilter}
                        accessibilityRole="button"
                        accessibilityLabel={t(typeFilterOpen ? "mapFilterClose" : "mapFilterOpen")}
                        accessibilityState={{ expanded: typeFilterOpen }}>
                        {typeFilterOpen ? (
                            <IconClose style={typeFilterOpen ? { ...style.filterFabIcon, ...style.filterFabIconOpen } : style.filterFabIcon} />
                        ) : (
                            <MapFilter style={typeFilterOpen ? { ...style.filterFabIcon, ...style.filterFabIconOpen } : style.filterFabIcon} />
                        )}
                    </TouchableOpacity>
                </View>

                <View style={style.toolDock}>
                    <TouchableOpacity
                        style={mapSelectorOpen ? [style.filterFab, style.filterFabOpen] : style.filterFab}
                        onPress={toggleMapSelector}
                        accessibilityRole="button"
                        accessibilityLabel={t("mapChooserOpen")}
                        accessibilityState={{ expanded: mapSelectorOpen }}>
                        <MapSettings style={mapSelectorOpen ? { ...style.filterFabIcon, ...style.filterFabIconOpen } : style.filterFabIcon} />
                    </TouchableOpacity>
                </View>

                <View style={style.zoomDock}>
                    <TouchableOpacity style={style.zoomDockButton} onPress={onZoomOut} accessibilityRole="button" accessibilityLabel="-">
                        <ThemedText style={style.zoomButtonText}>-</ThemedText>
                    </TouchableOpacity>
                    <View style={style.zoomDivider} />
                    <TouchableOpacity style={style.zoomDockButton} onPress={onZoomIn} accessibilityRole="button" accessibilityLabel="+">
                        <ThemedText style={style.zoomButtonText}>+</ThemedText>
                    </TouchableOpacity>
                </View>
            </View>

            {!(pois || paths || aois) && (
                <View style={style.loadingOverlay}>
                    <LoadingSpinner style={style.spinner} />
                </View>
            )}
        </View>
    );
};

export { MapPanel };
